import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPackageById } from '../../services/pricingService';
import { getuserdata } from '../../services/apiService';
import { getSettings } from '../../services/settingsService';
import { oneKlicks } from '../../SDK/oneclick';
import { useCurrency } from '../../context/CurrencyContext';

const PackageCheckout = () => {
    const { packageId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [pkg, setPkg] = useState(null);
    const [userdata, setUserdata] = useState({});
    const [razorpayKey, setRazorpayKey] = useState('rzp_live_SxrHxAcaqcutXT');
    const [logoUrl, setLogoUrl] = useState('https://apis.oneklicks.com/uploads/logo.png');
    const [processing, setProcessing] = useState(false);
    const { selectedCurrency, convertPrice } = useCurrency();
    const [displayPrice, setDisplayPrice] = useState(null);

    const getCurrencySymbol = (currency) => {
        const currencyMap = {
            'USD': '$', 'EUR': '€', 'GBP': '£', 'INR': '₹',
            'JPY': '¥', 'AUD': 'A$', 'CAD': 'C$'
        };
        return currencyMap[currency] || '₹';
    };

    useEffect(() => {
        const loadCheckoutData = async () => {
            setLoading(true);
            try {
                // Load User
                const user = await getuserdata('full');
                if (user) setUserdata(user);

                // Load Settings for Razorpay Key and Logo
                const settingsData = await getSettings();
                if (settingsData) {
                    if (settingsData.razorpay_key_id) {
                        setRazorpayKey(settingsData.razorpay_key_id);
                    }
                    const dbLogo = settingsData.site_logo_url || settingsData.logo_url;
                    if (dbLogo) {
                        setLogoUrl(dbLogo.startsWith('http') ? dbLogo : `${process.env.REACT_APP_API_BASE_LIVE_URL || 'https://apis.oneklicks.com'}/${dbLogo.replace(/^\/+/, '')}`);
                    }
                }

                // Load Razorpay Script
                if (!window.Razorpay) {
                    const script = document.createElement('script');
                    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
                    document.body.appendChild(script);
                }

                // Load Package
                const packageData = await getPackageById(packageId);
                if (packageData) {
                    setPkg(packageData);
                    // Handle currency conversion
                    if (packageData.baseCurrency === selectedCurrency) {
                        setDisplayPrice(packageData.price);
                    } else {
                        const converted = await convertPrice(packageData.price, packageData.baseCurrency, selectedCurrency);
                        setDisplayPrice(converted);
                    }
                } else {
                    alert("Package not found");
                    navigate('/doctor/packages');
                }
            } catch (error) {
                console.error("Error loading checkout data", error);
                alert("Error loading checkout details");
                navigate('/doctor/packages');
            } finally {
                setLoading(false);
            }
        };

        loadCheckoutData();
    }, [packageId, selectedCurrency]);

    const handlePayment = () => {
        if (!window.Razorpay) {
            alert("Payment gateway is still loading. Please try again in a few seconds.");
            return;
        }

        setProcessing(true);

        const options = {
            key: razorpayKey,
            amount: Math.round(displayPrice * 100), // convert to paise/cents
            currency: selectedCurrency,
            name: 'MeddyList',
            description: `Subscription for ${pkg.name} Package`,
            image: logoUrl,
            handler: async function (response) {
                if (response.razorpay_payment_id) {
                    try {
                        const startDate = new Date();
                        const endDate = new Date();
                        const days = !isNaN(pkg.period) ? parseInt(pkg.period) : 30;
                        endDate.setDate(startDate.getDate() + days);

                        const userPkgData = {
                            user_id: userdata.id,
                            package_id: pkg.id,
                            status: 'active',
                            start_date: startDate.toISOString().split('T')[0],
                            end_date: endDate.toISOString().split('T')[0],
                            transaction_id: response.razorpay_payment_id,
                            amount_paid: displayPrice
                        };

                        const saveResponse = await oneKlicks.create('', 'user_package', userPkgData, {});

                        if (saveResponse.success) {
                            try {
                                await oneKlicks.update('', 'users', { package_id: pkg.id }, { id: userdata.id });
                            } catch (updateErr) {
                                console.error("Error updating user's package:", updateErr);
                            }
                            alert(`Success! You have successfully subscribed to the ${pkg.name} package.`);
                            navigate('/doctor/packages'); // Or redirect to dashboard/settings
                        } else {
                            alert('Payment was successful, but there was an issue saving your subscription. Please contact support.');
                            setProcessing(false);
                        }
                    } catch (error) {
                        console.error("Error saving package:", error);
                        alert('Payment was successful, but an error occurred saving the package.');
                        setProcessing(false);
                    }
                }
            },
            prefill: {
                name: userdata.display_name || userdata.first_name || userdata.name || '',
                contact: userdata.phone || '',
                email: userdata.email || ''
            },
            theme: {
                color: '#4299e1'
            },
            modal: {
                ondismiss: function () {
                    setProcessing(false);
                }
            }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
            alert(`Payment failed: ${response.error.description}`);
            setProcessing(false);
        });
        rzp.open();
    };

    if (loading) {
        return (
            <div className="subbody" style={{ width: '100%', padding: '2% 4%', textAlign: 'center' }}>
                <div style={{ padding: '50px' }}>
                    <div className="loading-spinner">⏳</div>
                    <p style={{ marginTop: '20px' }}>Loading checkout details...</p>
                </div>
            </div>
        );
    }

    if (!pkg) return null;

    return (
        <div className="dc-haslayout dc-main-section" style={{ padding: '30px', backgroundColor: '#f4f7f6', minHeight: '80vh' }}>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12 mb-4">
                        <div className="dc-dashboardboxtitle">
                            <h2>Complete Your Subscription</h2>
                            <p style={{ color: '#64748b', fontSize: '15px' }}>Review your selected package and proceed to secure payment.</p>
                        </div>
                    </div>
                </div>

                <div className="row">
                    {/* Left Column: Package Details */}
                    <div className="col-12 col-lg-7 mb-4">
                        <div className="dc-dashboardbox" style={{ padding: '30px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                            <div className="d-flex align-items-center mb-4">
                                <div style={{
                                    width: '60px', height: '60px', borderRadius: '12px', backgroundColor: '#e3f2fd',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '20px'
                                }}>
                                    <span style={{ fontSize: '28px' }}>📦</span>
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 'bold', color: '#1e293b' }}>{pkg.name} Package</h3>
                                    <span style={{ color: '#64748b', fontSize: '14px', fontWeight: '500' }}>Valid for {pkg.period} days</span>
                                </div>
                            </div>

                            <hr style={{ borderColor: '#e2e8f0', margin: '25px 0' }} />

                            <h4 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: '#334155' }}>What's Included</h4>
                            <div className="row">
                                <div className="col-sm-6">
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                        <li style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#475569' }}>Professional Website:</span>
                                            <strong style={{ color: pkg.professionalwebsite?.trim().toLowerCase() === "yes" ? '#10b981' : '#ef4444' }}>
                                                {pkg.professionalwebsite?.trim().toLowerCase() === "yes" ? `✓ (${pkg.website_pages} pages)` : '✗'}
                                            </strong>
                                        </li>
                                        <li style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#475569' }}>SMS:</span>
                                            <strong style={{ color: '#1e293b' }}>{pkg.sms || '-'}</strong>
                                        </li>
                                        <li style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#475569' }}>WhatsApp Messages:</span>
                                            <strong style={{ color: '#1e293b' }}>{pkg.whatsup_count || '-'}</strong>
                                        </li>
                                        <li style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#475569' }}>Emails:</span>
                                            <strong style={{ color: '#1e293b' }}>{pkg.email || '-'}</strong>
                                        </li>
                                        <li style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#475569' }}>Patient Count:</span>
                                            <strong style={{ color: '#1e293b' }}>{pkg.patient_count || '-'}</strong>
                                        </li>
                                        <li style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#475569' }}>Social Posts:</span>
                                            <strong style={{ color: '#1e293b' }}>{pkg.socialposts || '-'}</strong>
                                        </li>
                                    </ul>
                                </div>
                                <div className="col-sm-6">
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                        <li style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#475569' }}>Social Videos:</span>
                                            <strong style={{ color: '#1e293b' }}>{pkg.socialvideos || '-'}</strong>
                                        </li>
                                        <li style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#475569' }}>Auto Post:</span>
                                            <strong style={{ color: pkg.autopost?.trim().toLowerCase() === "yes" ? '#10b981' : '#ef4444' }}>
                                                {pkg.autopost?.trim().toLowerCase() === "yes" ? '✓' : '✗'}
                                            </strong>
                                        </li>
                                        <li style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#475569' }}>Chat Support:</span>
                                            <strong style={{ color: pkg.chatsupport?.trim().toLowerCase() === "yes" ? '#10b981' : '#ef4444' }}>
                                                {pkg.chatsupport?.trim().toLowerCase() === "yes" ? '✓' : '✗'}
                                            </strong>
                                        </li>
                                        <li style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#475569' }}>HMS Integration:</span>
                                            <strong style={{ color: pkg.hms?.trim().toLowerCase() === "yes" ? '#10b981' : '#ef4444' }}>
                                                {pkg.hms?.trim().toLowerCase() === "yes" ? '✓' : '✗'}
                                            </strong>
                                        </li>
                                        <li style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#475569' }}>Featured Profile:</span>
                                            <strong style={{ color: pkg.featuredprofile?.trim().toLowerCase() === "yes" ? '#10b981' : '#ef4444' }}>
                                                {pkg.featuredprofile?.trim().toLowerCase() === "yes" ? '✓' : '✗'}
                                            </strong>
                                        </li>
                                        <li style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#475569' }}>Featured Days:</span>
                                            <strong style={{ color: '#1e293b' }}>{pkg.featureddays || '-'}</strong>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Payment Summary */}
                    <div className="col-12 col-lg-5">
                        <div className="dc-dashboardbox" style={{ padding: '30px', borderRadius: '12px', backgroundColor: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 10px 25px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '25px', color: '#1e293b' }}>Order Summary</h3>

                            <div style={{ backgroundColor: '#f8fafc', padding: '20px', borderRadius: '8px', marginBottom: '25px' }}>
                                <div className="d-flex justify-content-between mb-3">
                                    <span style={{ color: '#64748b', fontSize: '15px' }}>Package Cost</span>
                                    <span style={{ fontWeight: '600', color: '#1e293b' }}>{getCurrencySymbol(selectedCurrency)} {displayPrice?.toFixed(2)}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-3">
                                    <span style={{ color: '#64748b', fontSize: '15px' }}>Duration</span>
                                    <span style={{ fontWeight: '500', color: '#1e293b' }}>{pkg.period} Days</span>
                                </div>
                                <hr style={{ borderColor: '#e2e8f0' }} />
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <span style={{ color: '#0f172a', fontWeight: 'bold', fontSize: '16px' }}>Total to Pay</span>
                                    <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
                                        {getCurrencySymbol(selectedCurrency)} {displayPrice?.toFixed(2)}
                                    </span>
                                </div>
                            </div>

                            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '15px', color: '#334155' }}>Billing Details</h4>
                            <div style={{ marginBottom: '30px', fontSize: '14px', color: '#475569' }}>
                                <div style={{ display: 'flex', marginBottom: '8px' }}>
                                    <span style={{ width: '80px', color: '#94a3b8' }}>Name:</span>
                                    <strong style={{ color: '#1e293b' }}>{userdata.display_name || userdata.first_name || userdata.name}</strong>
                                </div>
                                <div style={{ display: 'flex', marginBottom: '8px' }}>
                                    <span style={{ width: '80px', color: '#94a3b8' }}>Email:</span>
                                    <strong style={{ color: '#1e293b' }}>{userdata.email}</strong>
                                </div>
                                <div style={{ display: 'flex' }}>
                                    <span style={{ width: '80px', color: '#94a3b8' }}>Phone:</span>
                                    <strong style={{ color: '#1e293b' }}>{userdata.phone}</strong>
                                </div>
                            </div>

                            <button
                                className="dc-btn"
                                onClick={handlePayment}
                                disabled={processing}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                {processing ? 'Processing Secure Payment...' : `Complete Payment`}
                            </button>

                            <div className="text-center mt-3" style={{ color: '#64748b', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ marginRight: '6px' }}>🔒</span> 100% Secure & Encrypted by Razorpay
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PackageCheckout;
